import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runPathfinder } from '../../../api/educentialApi';
import {
  Check,
  ChevronRight,
  ChevronLeft,
  GraduationCap,
  Target,
  Clock,
  Languages,
  MapPin,
  Award
} from 'lucide-react';

const FORM_OPTIONS = {
  qualification: [
    { value: 'SECONDARY_SCHOOL', label: 'Secondary School', subtitle: 'WASSCE/NECO/IGCSE' },
    { value: 'ND_OND', label: 'National Diploma', subtitle: 'ND/OND' },
    { value: 'HND', label: 'Higher National Diploma', subtitle: 'HND' },
    { value: 'ASSOCIATE_DEGREE', label: 'Associate Degree', subtitle: '2-year degree' },
    { value: 'BACHELORS_DEGREE', label: "Bachelor's Degree", subtitle: 'Undergraduate degree' },
    { value: 'MASTERS_DEGREE', label: "Master's Degree", subtitle: 'Postgraduate degree' },
    { value: 'NONE_OTHER', label: 'None/Other', subtitle: 'No formal qualification' },
  ],
  sameField: [
    { value: 'YES', label: 'Yes', subtitle: 'Continue in my current field' },
    { value: 'NO', label: 'No', subtitle: 'I want to switch fields' },
  ],
  academicGrade: [
    { value: 'FIRST_CLASS', label: 'Distinction / First Class', subtitle: '70% and above' },
    { value: 'SECOND_CLASS_UPPER', label: 'Second Class Upper', subtitle: '60-69%' },
    { value: 'SECOND_CLASS_LOWER', label: 'Second Class Lower', subtitle: '50-59%' },
    { value: 'THIRD_CLASS_PASS', label: 'Third Class / Pass', subtitle: '40-49%' },
  ],
  primaryGoal: [
    { value: 'BEGIN_UNI', label: 'Begin University Degree', subtitle: 'Start undergraduate studies' },
    { value: 'UPGRADE_HND', label: 'Upgrade HND to Degree', subtitle: 'Top-up programme' },
    { value: 'PROFESSIONAL_LICENSE', label: 'Professional UK License', subtitle: 'NMC, HCPC, SRA, etc.' },
    { value: 'MASTERS', label: "Master's Degree", subtitle: 'Postgraduate studies' },
    { value: 'SWITCH_CAREER', label: 'Switch Careers', subtitle: 'Change to different field' },
    { value: 'RESEARCH', label: 'Research Degree', subtitle: 'PhD/MPhil' },
  ],
  programmeType: [
    { value: 'COURSEWORK', label: 'Coursework-Based', subtitle: 'Lectures, assignments, exams' },
    { value: 'RESEARCH', label: 'Research-Based', subtitle: 'Independent research project' },
    { value: 'UNSURE', label: "I'm Not Sure", subtitle: 'Help me decide' },
  ],
  duration: [
    { value: 'LESS_THAN_ONE_YEAR', label: 'Less than 1 Year', subtitle: 'Short course' },
    { value: 'ONE_YEAR', label: '1 Year', subtitle: 'Standard masters' },
    { value: 'TWO_YEARS', label: '2 Years', subtitle: 'Extended programme' },
    { value: 'THREE_PLUS_YEARS', label: '3+ Years', subtitle: 'Undergraduate or PhD' },
  ],
  allowDependents: [
    { value: true, label: 'Yes', subtitle: 'I plan to bring family' },
    { value: false, label: 'No', subtitle: 'Studying alone' },
  ],
  workAfterStudy: [
    { value: 'YES', label: 'Yes', subtitle: 'I want to work while studying' },
    { value: 'NO', label: 'No', subtitle: 'Focus on studies only' },
    { value: 'UNDECIDED', label: 'Undecided', subtitle: 'Not sure yet' },
  ],
  englishEvidence: [
    { value: 'WAEC', label: 'WAEC English', subtitle: 'C6 or above' },
    { value: 'NECO', label: 'NECO English', subtitle: 'C6 or above' },
    { value: 'MEDIUM_OF_INSTRUCTION', label: 'Medium of Instruction', subtitle: 'Letter from university' },
    { value: 'IELTS', label: 'IELTS Academic', subtitle: 'International test' },
    { value: 'TOEFL', label: 'TOEFL iBT', subtitle: 'Internet-based test' },
    { value: 'PTE', label: 'PTE Academic', subtitle: 'Pearson test' },
    { value: 'NONE', label: "I Don't Have Any Yet", subtitle: 'Need to take test' },
  ],
  englishConfidence: [
    { value: 'YES', label: 'Yes', subtitle: 'I meet the requirements' },
    { value: 'NO', label: 'No', subtitle: 'I need improvement' },
    { value: 'UNSURE', label: 'Not Sure', subtitle: 'Need guidance' },
  ],
  fieldOfStudy: [
    { value: 'HEALTH_ALLIED_SCIENCES', label: 'Health & Allied Sciences', subtitle: 'Nursing, OT, PT, Radiography' },
    { value: 'ENGINEERING_TECH', label: 'Engineering & Technology', subtitle: 'Technical fields' },
    { value: 'DATA_AI_CS', label: 'Data, AI & Computer Science', subtitle: 'Technology & computing' },
    { value: 'BUSINESS_MANAGEMENT', label: 'Business & Management', subtitle: 'Finance, accounting, MBA' },
    { value: 'LAW_SOCIAL_SCIENCE', label: 'Law & Social Sciences', subtitle: 'Legal & social studies' },
    { value: 'ARTS_HUM_EDU', label: 'Arts, Humanities & Education', subtitle: 'Creative & teaching' },
    { value: 'OTHER', label: 'Other Field', subtitle: 'Specify later' },
  ],
  leadsToRegistration: [
    { value: 'YES', label: 'Yes', subtitle: 'NMC, HCPC, SRA, etc.' },
    { value: 'NO', label: 'No', subtitle: 'No professional body' },
    { value: 'UNSURE', label: 'Not Sure', subtitle: 'Need information' },
  ],
  postStudyIntentions: [
    { value: 'STAY_UK', label: 'Stay in the UK', subtitle: 'Work in the UK' },
    { value: 'MOVE_OTHER_COUNTRY', label: 'Move to Another Country', subtitle: 'Canada, Australia, etc.' },
    { value: 'RETURN_HOME', label: 'Return Home', subtitle: 'Back to home country' },
    { value: 'UNSURE', label: "I'm Not Sure", subtitle: 'Decide later' },
  ],
};

const STEPS = [
  {
    id: 1,
    title: 'Academic Background',
    subtitle: 'Tell us about your qualifications',
    icon: GraduationCap,
    fields: ['qualification', 'sameField', 'academicGrade'],
  },
  {
    id: 2,
    title: 'Study Goals',
    subtitle: 'What do you want to achieve?',
    icon: Target,
    fields: ['primaryGoal', 'programmeType'],
  },
  {
    id: 3,
    title: 'Preferences',
    subtitle: 'Your study and lifestyle preferences',
    icon: Clock,
    fields: ['duration', 'allowDependents', 'workAfterStudy'],
  },
  {
    id: 4,
    title: 'English Proficiency',
    subtitle: 'Your English language qualifications',
    icon: Languages,
    fields: ['englishEvidence', 'englishConfidence'],
  },
  {
    id: 5,
    title: 'Field of Study',
    subtitle: 'What subject interests you?',
    icon: Award,
    fields: ['fieldOfStudy', 'leadsToRegistration'],
  },
  {
    id: 6,
    title: 'Post-Study Plans',
    subtitle: 'What are your plans after graduation?',
    icon: MapPin,
    fields: ['postStudyIntentions'],
  },
];

const initialFormState = {
  qualification: '',
  sameField: '',
  academicGrade: '',
  primaryGoal: '',
  programmeType: '',
  duration: '',
  allowDependents: null,
  workAfterStudy: '',
  englishEvidence: [],
  englishConfidence: '',
  fieldOfStudy: '',
  leadsToRegistration: '',
  postStudyIntentions: '',
};

export default function UKPathfinder() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const currentStepData = STEPS.find(s => s.id === currentStep);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleEnglishToggle = (value) => {
    setFormData(prev => {
      let updated = [...prev.englishEvidence];

      if (value === 'NONE') {
        updated = updated.includes('NONE') ? [] : ['NONE'];
      } else {
        updated = updated.filter(item => item !== 'NONE');
        if (updated.includes(value)) {
          updated = updated.filter(item => item !== value);
        } else {
          updated.push(value);
        }
      }

      return { ...prev, englishEvidence: updated };
    });
    setError('');
  };

  const validateStep = () => {
    const stepFields = currentStepData.fields;

    for (const field of stepFields) {
      if (field === 'englishEvidence') {
        if (formData[field].length === 0) return false;
      } else if (field === 'allowDependents') {
        if (formData[field] === null) return false;
      } else {
        if (!formData[field]) return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      setError('Please answer all questions before continuing');
      return;
    }

    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await runPathfinder('UK', formData);
      setResult(response);
    } catch (err) {
      console.error('Pathfinder error:', err);
      setError(err.response?.data?.error || 'Failed to get recommendations. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setFormData(initialFormState);
    setCurrentStep(1);
    setResult(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (result) {
    return <ResultsScreen result={result} onStartOver={handleStartOver} />;
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              UK Study Pathfinder
            </h2>
            <p className="text-gray-600">
              Answer a few questions to get personalized university recommendations
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Step {currentStep} of {STEPS.length}</p>
              <p className="text-xl font-semibold text-gray-900 mt-1">{currentStepData.title}</p>
              <p className="text-sm text-gray-600 mt-0.5">{currentStepData.subtitle}</p>
            </div>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 border-2 border-yellow-200">
              <currentStepData.icon className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="relative">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-4">
              {STEPS.map((step) => (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    currentStep > step.id
                      ? 'bg-yellow-500 text-white'
                      : currentStep === step.id
                      ? 'bg-yellow-100 text-yellow-600 border-2 border-yellow-500'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-12 shadow-sm">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <FormStep
            step={currentStep}
            formData={formData}
            onChange={handleChange}
            onEnglishToggle={handleEnglishToggle}
          />

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center space-x-2 px-6 py-3 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed text-gray-700 rounded-lg transition-all border border-gray-300"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Previous</span>
            </button>

            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-8 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all shadow-sm"
            >
              <span>{currentStep === STEPS.length ? (isSubmitting ? 'Getting Recommendations...' : 'Get Recommendations') : 'Next'}</span>
              {!isSubmitting && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormStep({ step, formData, onChange, onEnglishToggle }) {
  const stepData = STEPS.find(s => s.id === step);

  return (
    <div className="space-y-8">
      {stepData.fields.map((field) => (
        <FormField
          key={field}
          field={field}
          value={formData[field]}
          onChange={onChange}
          onEnglishToggle={onEnglishToggle}
        />
      ))}
    </div>
  );
}

function FormField({ field, value, onChange, onEnglishToggle }) {
  const options = FORM_OPTIONS[field];

  const getFieldLabel = (field) => {
    const labels = {
      qualification: 'What is your highest qualification?',
      sameField: 'Do you want to study in the same field?',
      academicGrade: 'What was your academic grade?',
      primaryGoal: 'What is your primary goal for studying in the UK?',
      programmeType: 'What type of programme do you prefer?',
      duration: 'How long do you want to study?',
      allowDependents: 'Do you plan to bring dependents (spouse/children)?',
      workAfterStudy: 'Do you want to work during your studies?',
      englishEvidence: 'What English language evidence do you have? (Select all that apply)',
      englishConfidence: 'Are you confident you meet the English requirements?',
      fieldOfStudy: 'Which field do you want to study?',
      leadsToRegistration: 'Does your desired programme lead to professional registration?',
      postStudyIntentions: 'What are your plans after graduation?',
    };
    return labels[field] || field;
  };

  if (field === 'englishEvidence') {
    return (
      <div>
        <label className="block text-gray-900 font-semibold mb-4 text-lg">
          {getFieldLabel(field)}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onEnglishToggle(option.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">{option.label}</p>
                    {option.subtitle && (
                      <p className="text-gray-600 text-sm mt-1">{option.subtitle}</p>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-yellow-600 ml-2 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-gray-900 font-semibold mb-4 text-lg">
        {getFieldLabel(field)}
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(field, option.value)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">{option.label}</p>
                  {option.subtitle && (
                    <p className="text-gray-600 text-sm mt-1">{option.subtitle}</p>
                  )}
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-yellow-600 ml-2 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultsScreen({ result, onStartOver }) {
  const navigate = useNavigate();

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 border-2 border-green-500 mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Your Personalized Recommendations
          </h1>
          <p className="text-gray-600 text-lg">
            Based on your profile, we've found {result.recommendations?.length || 0} suitable pathways for you
          </p>
        </div>

        {/* Recommendations */}
        {result.recommendations && result.recommendations.length > 0 ? (
          <div className="space-y-6 mb-12">
            {result.recommendations.map((rec, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-8 hover:border-yellow-500 hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500 text-white font-bold text-sm">
                        {idx + 1}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-900">{rec.institution}</h3>
                    </div>
                    <p className="text-yellow-600 font-medium">{rec.program}</p>
                  </div>
                  <GraduationCap className="w-12 h-12 text-gray-200" />
                </div>

                {rec.reason && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{rec.reason}</p>
                  </div>
                )}

                {rec.details && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {rec.details.duration && (
                      <div>
                        <p className="text-gray-500 text-sm">Duration</p>
                        <p className="text-gray-900 font-medium">{rec.details.duration}</p>
                      </div>
                    )}
                    {rec.details.intakeMonths && (
                      <div>
                        <p className="text-gray-500 text-sm">Intake</p>
                        <p className="text-gray-900 font-medium">{rec.details.intakeMonths.join(', ')}</p>
                      </div>
                    )}
                    {rec.details.estimatedTuition && (
                      <div>
                        <p className="text-gray-500 text-sm">Tuition (approx)</p>
                        <p className="text-gray-900 font-medium">{rec.details.estimatedTuition}</p>
                      </div>
                    )}
                    {rec.details.englishRequirement && (
                      <div>
                        <p className="text-gray-500 text-sm">English Req</p>
                        <p className="text-gray-900 font-medium">{rec.details.englishRequirement}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center mb-12">
            <p className="text-gray-600 text-lg">No specific recommendations available at this time. Please contact us for personalized guidance.</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate('/overseas/uk/financial-analysis')}
            className="w-full sm:w-auto px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-all shadow-sm"
          >
            Calculate Study Costs
          </button>
          <button
            onClick={onStartOver}
            className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg transition-all border border-gray-300"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
